import { Add, Delete, Edit, Update } from '@mui/icons-material';
import { AppBar, Button, Box, Tab, Tabs, Theme, useMediaQuery, Tooltip } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { useSnackbar } from 'notistack';
import { useEffect, useMemo, useState } from 'react';
import { Page, PageTitleWithButton, Pagination, Table } from '../../../../components';
import { ExternalLink } from '../../../../components/link/ExternalLink';
import {
  getDocumentTemplateRoles,
  getMissingPandaDocTemplates,
  getVendorTypes,
  getReplacementFields,
  getTemplates,
  getAvailableTemplateTypes,
  deleteTemplate,
  getAdminSubcontractorsTypes
} from '../../../../fetch';
import { formatDate } from '../../../../helpers';
import {
  IAvailableTemplateTypes,
  IDocumentTemplate,
  IDocumentTemplateRole,
  IMissingDocumentTemplate,
  IReplacementField,
  ISortDirection,
  IVendorType
} from '../../../../models';
import { AssignModal } from '../assign-modal';
import { TemplateLibraryFilters } from '../template-library-filters';
import { DataFieldsList } from './DataFields/DataFieldsList';
import { MissingTemplateLibraryFilters } from '../missing-template-library-filters';
import { MobileContractTemplates } from './MobileContractTemplates';
import { MobileMissingContractTemplates } from './MobileMissingContractTemplates';

declare global {
  interface Window {
    PandaDoc: any | undefined;
  }
}

enum TemplateTabs {
  Assigned,
  Pending,
  ReplacementFields
}
export const ManageContractTemplates = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [contractTemplates, setContractTemplates] = useState<IDocumentTemplate[]>([]);
  const [currentTemplate, setCurrentTemplate] = useState<IDocumentTemplate | null>(null);
  const [isLoadingContractTemplates, setLoadingContractTemplates] = useState<boolean>(true);
  const [isDeletingTemplateAssign, setDeletingTemplateAssign] = useState<boolean>(false);
  const [page, setPage] = useState<number>(0);
  const [recordCount, setRecordCount] = useState<number>(0);
  const [perPage, setRowsPerPage] = useState<number>(10);
  const [isLoadingPartnerTypes, setIsLoadingPartnerTypes] = useState(false);
  const [partnerTypes, setPartnerTypes] = useState<IVendorType[] | null>(null);
  const [isLoadingTemplateTypes, setIsLoadingTemplateTypes] = useState(false);
  const [templateTypes, setTemplateTypes] = useState<IAvailableTemplateTypes[] | null>(null);
  const [selectedDocumentType, setDocumentType] = useState<string>('');
  const [partnerType, setPartnerType] = useState<string | undefined>('');
  const [isLoadingSubcontractorTypes, setIsLoadingSubcontractorTypes] = useState(false);
  const [subcontractorTypes, setSubcontractorTypes] = useState(null);
  const [subcontractorTypeId, setSubcontractorTypeId] = useState<string | number | undefined>('');
  const [searchValue, setSearchValue] = useState('');
  const [searchedValue, setSearchedValue] = useState('');
  const [selectedTab, setSelectedTab] = useState<number>(0);
  const [isLoadingMissingContractTemplates, setIsLoadingMissingContractTemplates] = useState(false);
  const [missingContractTemplates, setMissingContractTemplates] = useState<
    IMissingDocumentTemplate[]
  >([]);
  const [isAssignContractTemplateModalOpen, showAssignContractTemplateModal] = useState(false);
  const [missingTemplateSearch, setMissingTemplateSearch] = useState<string>('');
  const [filters, setFilters] = useState<{
    name?: number | string;
    vendorTypeId?: string;
    subcontractorTypeId?: number | string;
    type?: string;
  }>({});

  const [selectedSort, setSelectedSort] = useState<string>('Name');
  const [sortDirection, setSortDirection] = useState<{
    Name?: ISortDirection;
    VendorType?: ISortDirection;
    SubcontractorType?: ISortDirection;
  }>({
    Name: 'Asc'
  });
  const [loadingReplacementFields, setLoadingReplacementFields] = useState(true);
  const [loadingDocumentTemplateRoles, setLoadingDocumentTemplateRoles] = useState(true);
  const [replacementFields, setReplacementFields] = useState<IReplacementField[] | null>(null);
  const [documentTemplateRoles, setDocumentTemplateRoles] = useState<IDocumentTemplateRole[]>([]);

  const isMobile = useMediaQuery('(max-width: 960px)');
  const isAssignedMobile = useMediaQuery('(max-width: 1220px)');

  const classes = useStyles();

  const fetchMissingContractTemplates = async () => {
    try {
      setIsLoadingMissingContractTemplates(true);
      const res = await getMissingPandaDocTemplates({ query: missingTemplateSearch });

      setMissingContractTemplates(res);
    } catch (error) {
      enqueueSnackbar(
        error?.Detail ?? `Error loading Pending Contract Templates, please try again.`,
        {
          variant: 'error'
        }
      );
    } finally {
      setIsLoadingMissingContractTemplates(false);
    }
  };

  const fetchContractTemplates = async () => {
    try {
      setLoadingContractTemplates(true);
      const res = await getTemplates({
        page: page + 1,
        perPage: perPage,
        sortBy: selectedSort,
        sortDirection: sortDirection[selectedSort] as string,
        search: searchedValue,
        ...filters
      });
      setRecordCount(res.totalRecordCount);
      setContractTemplates(res.records);
    } catch (error) {
      enqueueSnackbar(`Error loading Contract Templates, please try again.`, {
        variant: 'error'
      });
      console.log(error);
    } finally {
      setLoadingContractTemplates(false);
    }
  };

  const fetchTemplateTypes = async () => {
    try {
      setIsLoadingTemplateTypes(true);
      const res = await getAvailableTemplateTypes({
        pandaDocTemplateId: currentTemplate?.pandaDocTemplateId
      });
      setTemplateTypes(res);
    } catch (error) {
      enqueueSnackbar(`Error loading template types, please try again.`, {
        variant: 'error'
      });
      console.log(error);
    } finally {
      setIsLoadingTemplateTypes(false);
    }
  };

  const fetchPartnerTypes = async () => {
    try {
      setIsLoadingPartnerTypes(true);
      const res = await getVendorTypes();
      setPartnerTypes(res.records);
    } catch (error) {
      enqueueSnackbar(`Error loading Partner Types, please try again.`, {
        variant: 'error'
      });
      console.log(error);
    } finally {
      setIsLoadingPartnerTypes(false);
    }
  };

  const fetchSubcontractorTypes = async () => {
    try {
      setIsLoadingSubcontractorTypes(true);
      const res = await getAdminSubcontractorsTypes();
      setSubcontractorTypes(res.records);
    } catch (error) {
      enqueueSnackbar(`Error loading subcontractor Types, please try again.`, {
        variant: 'error'
      });
      console.log(error);
    } finally {
      setIsLoadingSubcontractorTypes(false);
    }
  };

  const handleClickColumn = (column: string) => {
    setSelectedSort(column);
    setSortDirection({
      ...sortDirection,
      [column]: sortDirection[column] === 'Asc' ? 'Desc' : 'Asc'
    });
  };

  const fetchReplacementFields = async () => {
    try {
      setLoadingReplacementFields(true);
      const res = await getReplacementFields();
      setReplacementFields(res);
    } catch (error) {
      console.error(error);
      enqueueSnackbar(`Error loading replacement fields, please try again.`, {
        variant: 'error'
      });
    } finally {
      setLoadingReplacementFields(false);
    }
  };

  const fetchDocumentTemplateRoles = async () => {
    try {
      setLoadingDocumentTemplateRoles(true);
      const res = await getDocumentTemplateRoles();
      setDocumentTemplateRoles(res);
    } catch (error) {
      console.error(error);
      enqueueSnackbar(`Error loading document template roles, please try again.`, {
        variant: 'error'
      });
    } finally {
      setLoadingDocumentTemplateRoles(false);
    }
  };

  useEffect(() => {
    if (selectedTab === TemplateTabs.ReplacementFields) {
      fetchReplacementFields();
      fetchDocumentTemplateRoles();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTab]);

  useEffect(() => {
    if (selectedTab === TemplateTabs.Assigned) {
      fetchContractTemplates();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, perPage, filters, searchedValue, sortDirection, selectedSort, selectedTab]);

  useEffect(() => {
    if (selectedTab === TemplateTabs.Pending) {
      fetchMissingContractTemplates();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [missingTemplateSearch, selectedTab]);

  useEffect(() => {
    if (currentTemplate?.pandaDocTemplateId) {
      fetchTemplateTypes();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTemplate]);

  useEffect(() => {
    fetchTemplateTypes();
    fetchPartnerTypes();
    fetchSubcontractorTypes();
    fetchMissingContractTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDelete = async (template: IDocumentTemplate) => {
    const result = window.confirm('Are you sure you want to remove this assignment?');
    if (result) {
      try {
        setDeletingTemplateAssign(true);
        await deleteTemplate(template.pandaDocTemplateId);
        fetchContractTemplates();
        fetchMissingContractTemplates();
        enqueueSnackbar('Template assignment removed.', {
          variant: 'success'
        });
      } catch (error) {
        enqueueSnackbar(
          error?.Detail ?? `Error removing this template assignment, please try again.`,
          {
            variant: 'error'
          }
        );
      } finally {
        setDeletingTemplateAssign(false);
      }
    }
  };

  const handleUpdate = (template: IDocumentTemplate) => {
    setCurrentTemplate(template);
    showAssignContractTemplateModal(true);
  };

  const columns = useMemo(() => {
    return [
      {
        Header: 'Name',
        accessor: 'name',
        isServerSorted: selectedSort === 'Name',
        isServerSortedDesc: sortDirection.Name === 'Desc',
        handleClickColumn: () => handleClickColumn('Name')
      },
      {
        Header: 'Document Type',
        accessor: 'typeDisplayName',
        sort: false
      },
      {
        Header: 'Vendor Type',
        accessor: 'vendorType',
        isServerSorted: selectedSort === 'VendorType',
        isServerSortedDesc: sortDirection.VendorType === 'Desc',
        handleClickColumn: () => handleClickColumn('VendorType')
      },
      {
        Header: 'Subcontractor Type',
        accessor: 'subcontractorType',
        isServerSorted: selectedSort === 'SubcontractorType',
        isServerSortedDesc: sortDirection.SubcontractorType === 'Desc',
        handleClickColumn: () => handleClickColumn('SubcontractorType')
      },
      {
        id: 'actions',
        accessor: '',
        Header: '',
        sort: false,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IDocumentTemplate } };
        }) => {
          return (
            <Box display="flex">
              <Tooltip title="Edit PandaDoc Template">
                <div>
                  <ExternalLink
                    target={'_blank'}
                    to={`https://app.pandadoc.com/a/#/templates/${original.pandaDocTemplateId}`}
                  >
                    <Button id="edit" color="primary" startIcon={<Edit />}>
                      Edit
                    </Button>
                  </ExternalLink>
                </div>
              </Tooltip>
              <Tooltip title="Update Template Assignment">
                <Button
                  color="primary"
                  startIcon={<Update />}
                  onClick={() => {
                    handleUpdate(original);
                  }}
                >
                  Update
                </Button>
              </Tooltip>
              <Button
                color="error"
                startIcon={<Delete />}
                onClick={() => {
                  handleDelete(original);
                }}
              >
                Unassign
              </Button>
            </Box>
          );
        }
      }
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contractTemplates, selectedSort, sortDirection]);

  const missingTemplateColumns = useMemo(() => {
    return [
      {
        Header: 'Name',
        accessor: 'name',
        sort: false
      },
      {
        Header: 'Date Created',
        accessor: 'date_created',
        sort: false,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IMissingDocumentTemplate } };
        }) => {
          return <span>{formatDate(original.date_created)}</span>;
        }
      },
      {
        id: 'actions',
        accessor: '',
        Header: '',
        sort: false,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IMissingDocumentTemplate } };
        }) => {
          return (
            <Box display="flex">
              <ExternalLink
                target={'_blank'}
                to={`https://app.pandadoc.com/a/#/templates/${original.id}`}
              >
                <Button id="edit" color="primary" startIcon={<Edit />}>
                  Edit PandaDoc Template
                </Button>
              </ExternalLink>
            </Box>
          );
        }
      }
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contractTemplates]);
  return (
    <>
      <Page title="Manage Contract Templates">
        <PageTitleWithButton
          pageTitleBackProps={{
            showDivider: false,
            backText: 'Back to Admin home',
            backUrl: '/admin',
            title: 'Manage Contract Templates'
          }}
          button={
            <>
              {selectedTab === TemplateTabs.Pending && (
                <ExternalLink
                  target={'_blank'}
                  to={
                    'https://app.pandadoc.com/a/#/templates-next?sortBy=name&direction=asc&displayMode=folders_first&mainFilter=all'
                  }
                >
                  <Button
                    id="add-contract-template"
                    startIcon={<Add />}
                    className={classes.addRoleButton}
                    color="primary"
                    variant="contained"
                  >
                    Add Contract Template
                  </Button>
                </ExternalLink>
              )}
              {selectedTab === TemplateTabs.Assigned && (
                <Tooltip
                  title={templateTypes?.length === 0 ? 'No contract templates to assign' : ''}
                >
                  <Button
                    id="create-contract-template"
                    startIcon={<Add />}
                    onClick={() => {
                      showAssignContractTemplateModal(true);
                    }}
                    className={classes.addRoleButton}
                    color="primary"
                    variant="contained"
                    disabled={templateTypes?.length === 0}
                  >
                    Assign Contract Template
                  </Button>
                </Tooltip>
              )}
            </>
          }
        />
        <Box mt={-1}>
          <AppBar position="static" color="default" component="div" className={classes.tabBar}>
            <Tabs
              value={selectedTab}
              indicatorColor="primary"
              textColor="primary"
              onChange={(_, value) => {
                setSelectedTab(value);
              }}
            >
              <Tab id="assigned" aria-controls="assigned" label="Assigned" />
              <Tab id="pending" aria-controls="pending" label="Pending" />
              <Tab id="dataFields" aria-controls="dataFields" label="Replacement Fields" />
            </Tabs>
          </AppBar>
        </Box>
        {selectedTab === TemplateTabs.Pending && (
          <Box mt={1}>
            <MissingTemplateLibraryFilters
              isLoading={false}
              searchValue={missingTemplateSearch}
              setSearch={setMissingTemplateSearch}
            />
          </Box>
        )}
        {selectedTab === TemplateTabs.Assigned && (
          <Box mt={1}>
            <TemplateLibraryFilters
              isLoading={false}
              handleSearch={(clearSearch?: boolean) => {
                setPage(0);
                setFilters({
                  vendorTypeId: partnerType as string,
                  subcontractorTypeId,
                  type: selectedDocumentType || ''
                });
              }}
              applyFilters={(clearFilters?: boolean, clearSearch?: boolean) => {
                if (clearFilters) {
                  setFilters({
                    vendorTypeId: '',
                    subcontractorTypeId: undefined,
                    type: ''
                  });
                } else {
                  setPage(0);
                  setFilters({
                    ...filters,
                    vendorTypeId: partnerType || undefined,
                    subcontractorTypeId: subcontractorTypeId || undefined,
                    type: selectedDocumentType || ''
                  });
                }
              }}
              searchValue={searchValue}
              searchedValue={searchedValue}
              setSearch={setSearchValue}
              setSearchedValue={setSearchedValue}
              setPage={setPage}
              isLoadingPartnerTypes={isLoadingPartnerTypes}
              setPartnerType={setPartnerType}
              partnerType={partnerType}
              isLoadingSubcontractorTypes={isLoadingSubcontractorTypes}
              setSubcontractorTypeId={setSubcontractorTypeId}
              subcontractorTypeId={subcontractorTypeId}
              partnerTypes={partnerTypes}
              subcontractorTypes={subcontractorTypes}
              setDocumentType={setDocumentType}
              selectedDocumentType={selectedDocumentType}
              isLoadingTemplateTypes={isLoadingTemplateTypes}
              templateTypes={templateTypes}
            />
          </Box>
        )}
        {selectedTab === TemplateTabs.Pending && (
          <>
            <Table
              columns={missingTemplateColumns}
              data={missingContractTemplates}
              isLoading={isLoadingMissingContractTemplates}
              ResponsiveComponent={isMobile ? MobileMissingContractTemplates : undefined}
            />
          </>
        )}
        {selectedTab === TemplateTabs.Assigned && (
          <>
            <Table
              columns={columns}
              data={contractTemplates}
              isLoading={isLoadingContractTemplates || isDeletingTemplateAssign}
              hidePagination
              mobileBreakPoint={1220}
              mobileProps={{
                handleDelete: (val: IDocumentTemplate) => handleDelete(val),
                handleEdit: (val: IDocumentTemplate) => handleUpdate(val)
              }}
              ResponsiveComponent={isAssignedMobile ? MobileContractTemplates : undefined}
            />
            {!isLoadingContractTemplates && (
              <Pagination
                page={page}
                count={recordCount}
                rowsPerPage={perPage}
                setPage={setPage}
                setRowsPerPage={setRowsPerPage}
              />
            )}
          </>
        )}
        {selectedTab === TemplateTabs.ReplacementFields && (
          <>
            <DataFieldsList
              replacementFields={replacementFields}
              documentTemplateRoles={documentTemplateRoles}
              loadingReplacementFields={loadingReplacementFields}
              loadingDocumentTemplateRoles={loadingDocumentTemplateRoles}
            />
          </>
        )}

        {isAssignContractTemplateModalOpen && (
          <AssignModal
            open={true}
            onClose={(shouldUpdate?: boolean) => {
              showAssignContractTemplateModal(false);
              setCurrentTemplate(null);
              if (shouldUpdate) {
                fetchContractTemplates();
                fetchMissingContractTemplates();
              }
            }}
            missingDocs={missingContractTemplates}
            isLoadingMissingDocs={isLoadingMissingContractTemplates}
            isLoadingTypes={isLoadingTemplateTypes}
            types={templateTypes}
            currentOption={currentTemplate}
            isContractTemplate={true}
          />
        )}
      </Page>
    </>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  addRoleButton: {
    width: '100%',
    [theme.breakpoints.up('sm')]: {
      width: 'auto'
    }
  },
  tabBar: {
    backgroundColor: '#fff',
    boxShadow: 'none',
    borderBottom: `1px solid ${theme.palette.grey[400]}`
  }
}));
